import { PartyType } from './party';

/**
 * A tenant-configured counterparty a rating-matrix fee can be attributed to — a carrier, an
 * agent, the administrator, a platform or another party. One record is one real-world identity,
 * which may hold several of those roles.
 *
 * Never a seller: sellers are the existing Distributors, resolved per warranty (the quick-sale
 * distributor, or a cell's Seller element) — see {@link PARTY_RECORD_ROLES}.
 */
export interface PartyRecord {
  readonly id: string;
  readonly name: string;
  readonly roles: PartyType[];
  readonly created: Date;
  readonly updated: Date;
}

/** The roles a party record may hold — every assignable role except SELLER (sellers are Distributors). */
export const PARTY_RECORD_ROLES: readonly PartyType[] = [
  PartyType.INSURER,
  PartyType.AGENT,
  PartyType.ADMINISTRATOR,
  PartyType.PLATFORM,
  PartyType.OTHER,
];

/** The roles a rating-matrix fee may be attributed to. CUSTOMER and TAX_AUTHORITY are derived elsewhere, never configured. */
export const ASSIGNABLE_RECIPIENT_ROLES: readonly PartyType[] = [
  PartyType.SELLER,
  PartyType.INSURER,
  PartyType.AGENT,
  PartyType.ADMINISTRATOR,
  PartyType.PLATFORM,
  PartyType.OTHER,
];

/**
 * Who a Fee Type's (default) or rating-matrix cell's (override) amount is intended for.
 * Allocation metadata only — it never affects which cells match or what they price.
 *
 * - `role` SELLER: never a party record (sellers are Distributors). `partyRecordId` is null and
 *   the recipient is whichever seller the amount is for at pricing time —
 *   the cell's own Seller element when it has one (an upstream distributor's fee goes to that
 *   distributor), otherwise the warranty's seller. Lets one Fee Type default (e.g. Commission)
 *   pay every seller without configuring a record per distributor.
 * - any other role: `partyRecordId` is the {@link PartyRecord}, which must hold `role`.
 */
export interface RecipientAssignment {
  readonly role: PartyType;
  readonly partyRecordId: string | null;
}

/** Where a matrix cell's effective recipient came from. */
export enum RecipientSource {
  /** The cell's own override. */
  CELL = 'CELL',
  /** The cell's Fee Type default. */
  FEE_TYPE = 'FEE_TYPE',
  /**
   * Neither is configured, but the cell is scoped to a seller (has a Seller element) — that
   * distributor is the recipient. A configured recipient always takes precedence over this.
   */
  SELLER_DIMENSION = 'SELLER_DIMENSION',
  /** Neither is configured — the legacy INSURER attribution was applied and needs administrative review. */
  LEGACY_FALLBACK = 'LEGACY_FALLBACK',
  /** Neither is configured on a one-time (initiation) fee — owed to the ADMINISTRATOR by default. */
  ONE_TIME_DEFAULT = 'ONE_TIME_DEFAULT',
}

/** The cell override when set, otherwise the Fee Type default, otherwise none. */
export function resolveEffectiveRecipient(
  cellOverride: RecipientAssignment | null,
  feeTypeDefault: RecipientAssignment | null
): { assignment: RecipientAssignment; source: RecipientSource.CELL | RecipientSource.FEE_TYPE } | null {
  if (cellOverride) return { assignment: cellOverride, source: RecipientSource.CELL };
  if (feeTypeDefault) return { assignment: feeTypeDefault, source: RecipientSource.FEE_TYPE };
  return null;
}

/**
 * Returns why `assignment` is invalid for `record` (the record its `partyRecordId` references,
 * null when it references none or the record wasn't found), or null when it is valid.
 */
export function validateRecipientAssignment(
  assignment: RecipientAssignment,
  record: Pick<PartyRecord, 'roles'> | null
): string | null {
  if (!ASSIGNABLE_RECIPIENT_ROLES.includes(assignment.role)) {
    return `${assignment.role} cannot be assigned as a fee recipient.`;
  }
  if (assignment.role === PartyType.SELLER) {
    // Sellers are Distributors, resolved per warranty — never a party record.
    return assignment.partyRecordId ? 'A SELLER recipient never references a party.' : null;
  }
  if (!assignment.partyRecordId) return `A ${assignment.role} recipient must reference a party.`;
  if (!record) return `Party ${assignment.partyRecordId} was not found.`;
  if (!record.roles.includes(assignment.role)) {
    return `Party ${assignment.partyRecordId} does not hold the ${assignment.role} role.`;
  }
  return null;
}
